import { useCallback } from "react";
import type { Product } from "@/types";
import type { StoreCtx } from "@/store/appTypes";
import { removeProduct, saveNotification, saveProduct } from "@/store/api";
import type { CatalogImportResult, CatalogImportRow } from "@/utils/pdfCatalog";

// Acciones del catálogo: alta/edición de productos e importación desde PDF.
export const useCatalogActions = (ctx: StoreCtx) => {
  const { data, setData, run, refresh, setError } = ctx;

  const upsertProduct = useCallback(
    (product: Product) =>
      run(async () => {
        const saved = await saveProduct(product);
        // Aviso de desfase de precio: si el costo cargado es ≥ al precio público
        // (el margen no cuadra), le salta una alerta al admin para revisarlo.
        const mismatch = saved.pricePublic > 0 && saved.costBase >= saved.pricePublic;
        const alert = mismatch
          ? await saveNotification({
              audience: "admin",
              title: `Revisa el producto: ${saved.name}`,
              message: `El costo cargado (${saved.costBase}) es ≥ al precio público (${saved.pricePublic}). Hay un desfase de precio.`,
              kind: "info",
              date: new Date().toISOString(),
              read: false,
              link: "/admin/inventario",
            })
          : null;
        setData((p) => {
          const exists = p.products.some((x) => x.id === saved.id || x.sku === saved.sku);
          return {
            ...p,
            products: exists
              ? p.products.map((x) => (x.id === saved.id || x.sku === saved.sku ? saved : x))
              : [...p.products, saved],
            notifications:
              alert && !p.notifications.some((x) => x.id === alert.id)
                ? [alert, ...p.notifications]
                : p.notifications,
          };
        });
      }),
    [run, setData]
  );

  const deleteProduct = useCallback(
    (id: string) =>
      run(async () => {
        await removeProduct(id);
        setData((p) => ({
          ...p,
          products: p.products.filter((x) => x.id !== id),
          inventory: p.inventory.filter((x) => x.productId !== id),
        }));
      }),
    [run, setData]
  );

  // Importa el catálogo leído de un PDF con MERGE NO DESTRUCTIVO por SKU:
  //   · SKU nuevo            → crea el producto
  //   · SKU existente, cambia → actualiza precio/PV/nombre/foto (conserva el resto)
  //   · SKU idéntico          → omite (sin duplicar)
  // Nunca borra ventas, inventario ni historial previo.
  const importCatalogProducts = useCallback(
    async (
      rows: CatalogImportRow[],
      options?: { applyNewPrices?: boolean; applyPriceSkus?: string[] }
    ): Promise<CatalogImportResult | null> => {
      // Si el admin desmarca "aplicar precios nuevos", se conserva el precio
      // (y el costo base derivado) actual de cada producto existente. Los
      // productos NUEVOS siempre entran con el precio del PDF.
      const applyNewPrices = options?.applyNewPrices !== false;
      // Decisión producto por producto: si llega la lista de SKU a los que SÍ
      // se aplica el precio nuevo, manda sobre el flag global (permite aplicar
      // a unos y conservar el precio de otros en la misma importación).
      const perSku = Array.isArray(options?.applyPriceSkus)
        ? new Set(options.applyPriceSkus.map((s) => s.trim().toLowerCase()))
        : null;
      setError(null);
      const result: CatalogImportResult = {
        created: 0,
        updated: 0,
        images: 0,
        skipped: 0,
        warnings: [],
      };
      try {
        for (const row of rows) {
          const sku = row.sku.trim();
          if (!sku) continue;
          const existing = data.products.find(
            (p) => p.sku.trim().toLowerCase() === sku.toLowerCase()
          );

          if (!existing) {
            await saveProduct({
              id: "",
              sku,
              name: row.name || sku,
              category: row.category,
              pv: row.pv || 0,
              costBase: Math.round((row.pricePublic || 0) * 0.75),
              pricePublic: row.pricePublic || 0,
              imageUrl: row.imageUrl || undefined,
            });
            result.created += 1;
            if (row.imageUrl) result.images += 1;
            continue;
          }

          const shouldApplyPrice = perSku ? perSku.has(sku.toLowerCase()) : applyNewPrices;
          const priceChanged =
            shouldApplyPrice &&
            row.pricePublic > 0 &&
            Math.round(row.pricePublic) !== Math.round(existing.pricePublic);
          const pvChanged = row.pv > 0 && row.pv !== existing.pv;
          const nameChanged = !!row.name && row.name !== existing.name;
          const categoryChanged = !!row.category && row.category !== existing.category;
          const newImage = !!row.imageUrl && row.imageUrl !== (existing.imageUrl ?? "");

          // El costo base se recalcula como el 75% del precio de catálogo solo
          // cuando se aplica el precio nuevo; si se conserva el precio, también
          // se conserva el costo (no se renormaliza).
          const targetCostBase =
            shouldApplyPrice && row.pricePublic > 0
              ? Math.round(row.pricePublic * 0.75)
              : existing.costBase;
          const costChanged = existing.costBase !== targetCostBase;

          if (
            !priceChanged &&
            !pvChanged &&
            !nameChanged &&
            !categoryChanged &&
            !newImage &&
            !costChanged
          ) {
            result.skipped += 1;
            continue;
          }

          await saveProduct({
            ...existing,
            name: nameChanged ? row.name : existing.name,
            category: categoryChanged ? row.category : existing.category,
            pv: pvChanged ? row.pv : existing.pv,
            pricePublic: priceChanged ? row.pricePublic : existing.pricePublic,
            costBase: targetCostBase,
            imageUrl: newImage ? row.imageUrl : existing.imageUrl,
          });
          result.updated += 1;
          if (newImage) result.images += 1;
        }

        await refresh();
        setError(null);
        return result;
      } catch (e) {
        setError(e instanceof Error ? e.message : "No se pudo importar el catálogo desde el PDF.");
        return null;
      }
    },
    [data.products, refresh, setError]
  );

  return { upsertProduct, deleteProduct, importCatalogProducts };
};